import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import { logActivity } from "@/shared/utils/activityLogger";

import type { Database } from "@/shared/types/database.types";

type EstateContactInsert = Database["public"]["Tables"]["estate_contacts"]["Insert"];
type EstateContactUpdate = Database["public"]["Tables"]["estate_contacts"]["Update"];

export function useEstateContactMutations(estateId: number | undefined) {
  const { user } = useAppContext();
  const { isDemoMode } = useDemoMode();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["person-contacts", estateId] });
    queryClient.invalidateQueries({ queryKey: ["lawyer-person-contacts", estateId] });
  };

  const createContact = useMutation({
    mutationFn: async (contact: Omit<EstateContactInsert, "user_id" | "estate_id">) => {
      if (isDemoMode) {
        const fakeContact = {
          id: -Date.now(),
          estate_id: estateId!,
          user_id: null,
          source_document_id: null,
          source_document: null,
          extracted_from_document: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          ...contact,
          email: contact.email?.toLowerCase() ?? null,
        };
        queryClient.setQueryData<unknown[]>(["lawyer-person-contacts", estateId], (prev = []) => [
          fakeContact,
          ...prev,
        ]);
        return null;
      }
      if (!estateId) throw new Error("estateId is required");

      const normalizedEmail = contact.email?.toLowerCase() || null;

      if (normalizedEmail) {
        const { data: existingContact, error: checkError } = await supabase
          .from("estate_contacts")
          .select("id")
          .eq("estate_id", estateId)
          .eq("email", normalizedEmail)
          .maybeSingle();

        if (checkError && checkError.code !== "PGRST116") throw checkError;
        if (existingContact) throw new Error("A contact with this email already exists");
      }

      const { data, error } = await supabase
        .from("estate_contacts")
        .insert({
          ...contact,
          email: normalizedEmail,
          user_id: user?.user_id ?? null,
          estate_id: estateId,
        })
        .select()
        .single();

      if (error) throw error;

      if (user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "added_contact",
          actor_type: "lawyer",
          user_id: user.user_id,
          entity_type: "contact",
          entity_id: String(data.id),
          metadata: {
            name: [contact.first_name, contact.last_name].filter(Boolean).join(" "),
            relationship: contact.relationship ?? null,
            email: contact.email ?? null,
            legal_role: contact.legal_role ?? null,
          },
        }).catch(() => {});
      }

      return data;
    },
    onSuccess: () => {
      if (!isDemoMode) invalidate();
    },
  });

  const updateContact = useMutation({
    mutationFn: async ({ id, ...updates }: EstateContactUpdate & { id: number }) => {
      if (isDemoMode) {
        const normalizedUpdates = { ...updates };
        if (normalizedUpdates.email !== undefined) {
          normalizedUpdates.email = normalizedUpdates.email?.toLowerCase() ?? null;
        }
        queryClient.setQueryData<unknown[]>(["lawyer-person-contacts", estateId], (prev = []) =>
          prev.map((c) =>
            (c as { id: number }).id === id ? { ...(c as object), ...normalizedUpdates } : c,
          ),
        );
        return null;
      }
      if (!estateId) throw new Error("estateId is required");

      if (updates.email !== undefined) {
        updates.email = updates.email?.toLowerCase() || null;
      }

      const { data, error } = await supabase
        .from("estate_contacts")
        .update(updates)
        .eq("id", id)
        .eq("estate_id", estateId)
        .select()
        .single();

      if (error) throw error;

      if (user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "updated_contact",
          actor_type: "lawyer",
          user_id: user.user_id,
          entity_type: "contact",
          entity_id: String(id),
          metadata: { ...updates },
        }).catch(() => {});
      }

      return data;
    },
    onSuccess: () => {
      if (!isDemoMode) invalidate();
    },
  });

  const deleteContact = useMutation({
    mutationFn: async (id: number) => {
      if (isDemoMode) {
        queryClient.setQueryData<unknown[]>(["lawyer-person-contacts", estateId], (prev = []) =>
          prev.filter((c) => (c as { id: number }).id !== id),
        );
        return;
      }
      if (!estateId) throw new Error("estateId is required");

      const { data: contactSnapshot } = await supabase
        .from("estate_contacts")
        .select("first_name, last_name, relationship, email")
        .eq("id", id)
        .maybeSingle();

      const { error } = await supabase
        .from("estate_contacts")
        .delete()
        .eq("id", id)
        .eq("estate_id", estateId);

      if (error) throw error;

      if (user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "deleted_contact",
          actor_type: "lawyer",
          user_id: user.user_id,
          entity_type: "contact",
          entity_id: String(id),
          metadata: {
            name: contactSnapshot
              ? [contactSnapshot.first_name, contactSnapshot.last_name].filter(Boolean).join(" ") ||
                null
              : null,
            relationship: contactSnapshot?.relationship ?? null,
            email: contactSnapshot?.email ?? null,
          },
        }).catch(() => {});
      }
    },
    onSuccess: () => {
      if (!isDemoMode) invalidate();
    },
  });

  return {
    createContactAsync: createContact.mutateAsync,
    updateContactAsync: updateContact.mutateAsync,
    deleteContactAsync: deleteContact.mutateAsync,
    isCreating: createContact.isPending,
    isUpdating: updateContact.isPending,
    isDeleting: deleteContact.isPending,
  };
}
